import { createContext } from 'react';

// === AMPLITUDE REMOVED — uncomment the block below to re-enable Amplitude ===
// import { useEffect, useMemo, useRef, useState } from 'react';
// import { useCentralizedProviderData, useMe } from '@crehana/auth';
// import { getDeviceType, notEmpty } from '@crehana/utils';
// import Amplitude, { type AmplitudeClient } from 'amplitude-js';
// import { initConfig } from './config';
// === END AMPLITUDE REMOVED ===

import { isValidEventType, showLogs } from "./utils";

// Local stand-in for the Amplitude SDK client. Mirrors only the methods the
// (now commented-out) original implementation called, so the public shape of
// `AmplitudeContextValues.amplitude` keeps a typed contract for downstream
// consumers — most of which only do a truthy check, but a few destructure
// `amplitude`. When re-enabling Amplitude, delete this alias and restore the
// `AmplitudeClient` import from `amplitude-js`. See packages/analytics/AMPLITUDE_REENABLE.md.

/**
 * @deprecated Amplitude removed — context value is now inert. Kept for
 * backwards compatibility; will be deleted in a future release.
 */
import { jsx as _jsx } from "react/jsx-runtime";
/**
 * @deprecated Amplitude removed — context is now inert. Kept for backwards
 * compatibility; will be deleted in a future release.
 */
export var AmplitudeContext = /*#__PURE__*/createContext(undefined);

/**
 * @deprecated Amplitude removed — these props are now ignored. Kept for
 * backwards compatibility; will be deleted in a future release.
 */

// Lifted to module scope: zero component-level dependencies, so a stable
// reference prevents every `useAmplitude()` consumer from re-rendering each
// time `AmplitudeProvider` re-renders.
var noopLogEvent = function noopLogEvent(logEventConfig) {
  if (!isValidEventType(logEventConfig.event)) {
    return;
  }
  showLogs(logEventConfig);
  if (logEventConfig.callback) {
    logEventConfig.callback();
  }
};
var NOOP_AMPLITUDE_CONTEXT_VALUE = {
  amplitude: undefined,
  logEvent: noopLogEvent
};

/**
 * @deprecated Amplitude removed — provider is now a no-op that only renders
 * its children. The SDK is no longer initialized, `logEvent` does nothing
 * (aside from invoking the optional callback synchronously to preserve
 * navigate-after-track behavior). Remove call sites and we will delete this
 * in a future release.
 */
var AmplitudeProvider = function AmplitudeProvider(_ref) {
  var _apiKey = _ref.apiKey,
    _ref$platform = _ref.platform,
    _platform = _ref$platform === void 0 ? 'Web' : _ref$platform,
    children = _ref.children,
    _userProp = _ref.user;
  /* === AMPLITUDE REMOVED — uncomment the block below to re-enable Amplitude ===
  const isTest = process.env.NODE_ENV === 'test';
   const { me, loading: meLoading } = useMe();
  const { me: centralizedMe, loading: centralizedLoading } =
    useCentralizedProviderData();
  const amplitudeRef = useRef<AmplitudeClient>();
  const [initialized, setInitialized] = useState(false);
  const eventsQueue = useRef<Config[]>([]);
  const loading = meLoading || centralizedLoading;
   // The CDN may fail or the user may disable tracking
  const loadingFailed = useRef(false);
   const user: TLocalUserData = {
    isLoading: _userProp?.isLoading || loading,
    data: _userProp?.data || me,
    centralizedMe,
  };
   useEffect(() => {
    if (apiKey && !amplitudeRef.current) {
      const config: Amplitude.Config = {
        ...initConfig,
        onError: () => {
          loadingFailed.current = true;
           // Run all the callbacks if loading failed
          eventsQueue.current.forEach(({ callback }) => {
            // biome-ignore lint/style/useBlockStatements: single-line body is intentional
            if (callback) callback();
          });
          eventsQueue.current = [];
        },
      };
       // we don't need to initialize the amplitude client in test mode
      if (isTest) {
        amplitudeRef.current = {
          // biome-ignore lint/suspicious/noEmptyBlockStatements: intentional empty block
          logEvent: () => {},
          // biome-ignore lint/suspicious/noEmptyBlockStatements: intentional empty block
          setUserProperties: () => {},
          // biome-ignore lint/suspicious/noEmptyBlockStatements: intentional empty block
          setUserId: () => {},
        } as unknown as AmplitudeClient;
         setInitialized(true);
        console.log('Amplitude initialized');
      } else {
        Amplitude.getInstance().init(apiKey, undefined, config, instance => {
          amplitudeRef.current = instance;
          setInitialized(true);
          console.log('Amplitude initialized');
        });
      }
    }
  }, [apiKey]);
   // biome-ignore lint/correctness/useExhaustiveDependencies: dependencies are intentional
  useEffect(() => {
    if (initialized && user && !user.isLoading) {
      const userProperties: Record<string, unknown> = {};
       userProperties.Platform = platform;
      userProperties['Platform Device'] = getDeviceType();
      const userData = user.data;
       if (user.centralizedMe) {
        Object.assign(
          userProperties,
          buildCentralizedUserProperties(user.centralizedMe),
        );
      }
       if (userData?.originalId) {
        amplitudeRef.current!.setUserId(userData.originalId.toString());
         if (userData.segmentation) {
          userProperties['User Segment'] = userData.segmentation;
        }
        if (
          userData.segmentationKey !== null &&
          userData.segmentationKey !== undefined
        ) {
          userProperties['User Segment ID'] = userData.segmentationKey;
        }
        if (userData.tastes) {
          const tastes = userData.tastes.filter(notEmpty);
          // Remove repeated categories
          const categories = [
            ...new Set(tastes.map(t => t.parent?.name).filter(notEmpty)),
          ];
          const subCategories = tastes.map(t => t.name);
           if (categories.length) {
            userProperties['Categories Interest'] = categories;
          }
          if (subCategories.length) {
            userProperties['Subcategories Interest'] = subCategories;
          }
        }
        if (userData.occupation) {
          userProperties.Role = userData.occupation;
        }
        if (userData.birthday) {
          userProperties.Birthday = userData.birthday;
        }
        if (userData.goals) {
          const goals = userData.goals.map(g => g?.name).filter(notEmpty);
           if (goals.length) {
            userProperties.Objectives = goals;
          }
        }
        if (userData.membership?.membershipType?.tag) {
          userProperties['Premium Type'] =
            userData.membership.membershipType.tag;
        }
        if (userData.organization?.originalId) {
          userProperties['Organization Id'] = userData.organization?.originalId;
        }
        if (userData.organization?.slug) {
          userProperties['Organization Slug'] = userData.organization.slug;
        }
        if (userData.organization?.organizationType) {
          userProperties['Organization Type'] =
            userData.organization.organizationType;
        }
        if (userData.organization?.organizationSize) {
          userProperties['Organization Size'] =
            userData.organization.organizationSize;
        }
        if (userData.organization?.assignedType) {
          userProperties['Organization Assigned'] =
            userData.organization.assignedType;
        }
        if (userData.organization?.industry) {
          userProperties['Organization Industry'] =
            userData.organization.industry;
        }
        if (userData.organization?.configuration?.customerSuccessType) {
          userProperties['Organization Customer Success Type'] =
            userData.organization.configuration.customerSuccessType;
        }
        if (userData.organization?.startDate) {
          userProperties['Organization Start Date'] =
            userData.organization.startDate;
        }
         userProperties['Organization Studio Available'] = userData.organization
          ?.configuration?.hasStudioAvailable
          ? 'Yes'
          : 'No';
         userProperties['Organization Gamification Available'] = userData
          .organization?.configuration?.hasGamificationAvailable
          ? 'Yes'
          : 'No';
      }
      amplitudeRef.current!.setUserProperties(userProperties);
    }
    // biome-ignore lint/correctness/useExhaustiveDependencies: dependencies are intentional
  }, [user, initialized]);
   useEffect(() => {
    if (initialized && eventsQueue.current.length) {
      if (process.env.NODE_ENV === 'development') {
        console.group('Flushing Amplitude event queue');
        eventsQueue.current.forEach(({ event, data }) => {
          console.log('Event type:', event);
          console.log('Event data', data);
        });
        console.groupEnd();
      }
       // Flush queued events before the api loaded
      eventsQueue.current.forEach(logEventConfig => {
        showLogs(logEventConfig);
        amplitudeRef.current!.logEvent(
          logEventConfig.event,
          logEventConfig.data,
          logEventConfig.callback,
        );
      });
      eventsQueue.current = [];
    }
  }, [initialized]);
   const logEvent: LogEvent = logEventConfig => {
    if (!isValidEventType(logEventConfig.event)) {
      return;
    }
     if (loadingFailed.current && logEventConfig.callback) {
      logEventConfig.callback();
      return;
    }
     if (!amplitudeRef.current) {
      eventsQueue.current.push(logEventConfig);
      return;
    }
     showLogs(logEventConfig);
     amplitudeRef.current.logEvent(
      logEventConfig.event,
      logEventConfig.data,
      logEventConfig.callback,
    );
  };
   // biome-ignore lint/correctness/useExhaustiveDependencies: dependencies are intentional
  return useMemo(
    () => (
      <AmplitudeContext.Provider
        value={{
          amplitude: amplitudeRef.current,
          logEvent,
        }}
      >
        {children}
      </AmplitudeContext.Provider>
    ),
    [children, initialized],
  );
  === END AMPLITUDE REMOVED === */

  return /*#__PURE__*/_jsx(AmplitudeContext.Provider, {
    value: NOOP_AMPLITUDE_CONTEXT_VALUE,
    children: children
  });
};
export default AmplitudeProvider;